(function () {
  const Core = window.FoundiFunnelCore;
  if (!Core || customElements.get('foundi-cart')) return;

  customElements.define('foundi-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-foundi-cart-config]').textContent);
      this.panel = this.querySelector('[role="dialog"]');
      this.busy = false;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const path = new URL(form.action, location.href).pathname.replace(/\/$/, '');
        if (!path.endsWith('/cart/add')) return;
        event.preventDefault();
        if (!this.busy && form.reportValidity()) this.add(new FormData(form), form);
      }, options);

      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link || link.hasAttribute('data-foundi-noajax')) return;
        if (new URL(link.href, location.href).pathname.replace(/\/$/, '') !== `${this.config.root.replace(/\/$/, '')}/cart`) return;
        event.preventDefault();
        this.open();
        this.refresh().catch((error) => this.showError(error.message));
      }, options);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-foundi-cart-close]')) return this.close();
        const change = event.target.closest('[data-foundi-cart-change]');
        if (change && !this.busy) return this.change(change.dataset.key, Number(change.dataset.foundiCartChange));
        const upsell = event.target.closest('[data-foundi-upsell]');
        if (upsell && !this.busy) return this.applyUpsell(upsell.dataset.foundiUpsell);
      }, options);

      document.addEventListener('keydown', (event) => {
        if (this.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      }, options);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.ready = false;
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency, currencyDisplay: 'code' }).format(cents / 100);
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
    }

    async request(path, body) {
      const options = { headers: { Accept: 'application/json' } };
      if (body) {
        options.method = 'POST';
        if (body instanceof FormData) options.body = body;
        else {
          options.headers['Content-Type'] = 'application/json';
          options.body = JSON.stringify(body);
        }
      }
      const response = await fetch(`${this.config.root}${path}`, options);
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'We could not update your bag.');
      return data;
    }

    lock(active) {
      this.busy = active;
      this.panel.setAttribute('aria-busy', String(active));
      this.querySelectorAll('button,[name="checkout"]').forEach((button) => { button.disabled = active; });
      this.querySelector('[data-foundi-cart-status]').textContent = active ? 'Updating your bag…' : '';
    }

    open() {
      if (!this.hidden) return;
      this.returnFocus = document.activeElement;
      this.hidden = false;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.querySelector('[data-foundi-cart-close]').focus();
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    showError(message) {
      const error = this.querySelector('[data-foundi-cart-error]');
      error.hidden = false;
      error.textContent = message;
    }

    async refresh() {
      const cart = await this.request('cart.js');
      this.render(cart);
      return cart;
    }

    async add(formData, form) {
      if (!formData.get('selling_plan')) formData.delete('selling_plan');
      this.lock(true);
      try {
        await this.request('cart/add.js', formData);
        await this.refresh();
        this.open();
        this.querySelector('[data-foundi-cart-status]').textContent = 'Added to your bag.';
      } catch (error) {
        this.open();
        this.showError(error.message);
        const inline = form?.querySelector('[data-sq-error]');
        if (inline) { inline.style.display = 'block'; inline.textContent = error.message; }
      } finally {
        this.lock(false);
      }
    }

    async change(key, quantity) {
      this.lock(true);
      try {
        this.render(await this.request('cart/change.js', { id: key, quantity }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.lock(false);
      }
    }

    async applyUpsell(type) {
      const cart = await this.request('cart.js');
      const recommendation = Core.getCartUpsell(cart.items, this.config.variantIds);
      if (!recommendation || recommendation.type !== type) return;
      this.lock(true);
      try {
        if (type === 'pouch') {
          await this.request('cart/add.js', { items: [{ id: recommendation.variantId, quantity: 1 }] });
        } else {
          const current = cart.items.find((item) => String(item.variant_id) === String(recommendation.fromVariantId));
          const item = { id: recommendation.variantId, quantity: 1 };
          const planId = current?.selling_plan_allocation?.selling_plan?.id;
          if (planId) item.selling_plan = planId;
          await this.request('cart/add.js', { items: [item] });
          await this.request('cart/change.js', { id: current.key, quantity: 0 });
        }
        this.render(await this.request('cart.js'));
        this.querySelector('[data-foundi-cart-status]').textContent = type === 'upgrade' ? 'Bundle upgrade added.' : 'Full pouch added.';
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.lock(false);
      }
    }

    render(cart) {
      this.config.currency = cart.currency;
      this.currentCart = cart;
      this.querySelector('[data-foundi-cart-error]').hidden = true;
      document.querySelectorAll('[data-sq-count],[data-foundi-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
      this.querySelector('[data-foundi-cart-subtotal]').textContent = this.money(cart.total_price);
      this.querySelector('[data-foundi-cart-foot]').hidden = cart.item_count === 0;
      const e = (value) => this.escape(value);
      const items = this.querySelector('[data-foundi-cart-items]');
      items.innerHTML = cart.items.length ? cart.items.map((item) => `
        <article class="foundi-cart__line">
          ${item.image ? `<img src="${e(item.image)}" alt="" width="76" height="88">` : '<span aria-hidden="true">Foundi</span>'}
          <div>
            <a href="${e(item.url)}">${e(item.product_title)}</a>
            <small>${e(item.variant_title === 'Default Title' ? '' : item.variant_title)}</small>
            <small>${e(item.selling_plan_allocation?.selling_plan?.name || 'One-time purchase')}</small>
            <div class="foundi-cart__line-bottom"><div class="foundi-cart__step"><button type="button" data-key="${e(item.key)}" data-foundi-cart-change="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-key="${e(item.key)}" data-foundi-cart-change="${item.quantity + 1}" aria-label="Increase quantity">+</button></div><strong>${this.money(item.final_line_price)}</strong></div>
            <button class="foundi-cart__remove" type="button" data-key="${e(item.key)}" data-foundi-cart-change="0">Remove</button>
          </div>
        </article>`).join('') : '<p class="foundi-cart__empty">Your Foundi routine can start here.<a href="/#foundi-offer" data-foundi-noajax>Choose your pouch →</a></p>';

      const recommendation = Core.getCartUpsell(cart.items, this.config.variantIds);
      const upsell = this.querySelector('[data-foundi-cart-upsell]');
      if (!recommendation) {
        upsell.replaceChildren();
      } else if (recommendation.type === 'pouch') {
        upsell.innerHTML = `<h3>Ready for the full pouch?</h3><p>Move from the three-stick sample to 30 sachets in the full three-flavour variety pack.</p><button type="button" data-foundi-upsell="pouch">Add one pouch · ${this.money(this.config.prices.pouch)}</button>`;
      } else {
        upsell.innerHTML = `<h3>Make it a two-pouch bundle.</h3><p>Get 60 sachets and save ${this.money(this.config.savings.duo)} versus two single pouches.</p><button type="button" data-foundi-upsell="upgrade">Upgrade bundle · ${this.money(this.config.prices.duo)}</button>`;
      }
    }
  });
})();
